import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  AccountBalance as AccountBalanceIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface Supplier {
  id: string;
  nume: string;
  cui: string;
  nrRegCom: string;
  adresa: string;
  telefon: string;
  email: string;
  contBancar: string;
  banca: string;
  tip: 'furnizor' | 'client' | 'ambele';
  categorie: string;
  status: 'activ' | 'inactiv' | 'suspendat';
  rating: number;
  dataInregistrare: string;
  ultimaTranzactie?: string;
  totalTranzactii: number;
  valoareTotala: number;
  sold: number;
  observatii?: string;
  parohieId?: string;
  parohieNume?: string;
}

const Furnizori: React.FC = () => {
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTip, setFilterTip] = useState('');
  const [filterCategorie, setFilterCategorie] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'cards'>('list');

  // Mock data pentru furnizori
  const [suppliers] = useState<Supplier[]>([
    {
      id: '1',
      nume: 'SC Materiale Constructii SRL',
      cui: 'RO12345678',
      nrRegCom: 'J40/1234/2020',
      adresa: 'Str. Constructorilor nr. 15, București',
      telefon: '021-123-4567',
      email: 'contact@materiale-constructii.ro',
      contBancar: 'RO49BTRL1234567890123456',
      banca: 'Banca Transilvania',
      tip: 'furnizor',
      categorie: 'Materiale construcție',
      status: 'activ',
      rating: 4.5,
      dataInregistrare: '2020-01-15',
      ultimaTranzactie: '2024-01-15',
      totalTranzactii: 25,
      valoareTotala: 125000,
      sold: 15000,
      observatii: 'Furnizor de încredere pentru materiale de construcție',
      parohieId: 'parohia1',
      parohieNume: 'Parohia Sf. Nicolae'
    },
    {
      id: '2',
      nume: 'SC Utilitati Publice SA',
      cui: 'RO87654321',
      nrRegCom: 'J40/5678/2018',
      adresa: 'Bd. Unirii nr. 1, București',
      telefon: '021-987-6543',
      email: 'facturare@utilitati.ro',
      contBancar: 'RO12BRDE1234567890123456',
      banca: 'BRD',
      tip: 'furnizor',
      categorie: 'Utilități',
      status: 'activ',
      rating: 4.2,
      dataInregistrare: '2018-03-10',
      ultimaTranzactie: '2024-01-20',
      totalTranzactii: 48,
      valoareTotala: 45000,
      sold: 2500,
      observatii: 'Furnizor pentru servicii de utilități',
      parohieId: 'parohia1',
      parohieNume: 'Parohia Sf. Nicolae'
    },
    {
      id: '3',
      nume: 'SC Libraria Religioasa SRL',
      cui: 'RO11223344',
      nrRegCom: 'J40/9999/2019',
      adresa: 'Str. Cărților nr. 25, București',
      telefon: '021-555-7777',
      email: 'comenzi@libraria-religioasa.ro',
      contBancar: 'RO33INGB1234567890123456',
      banca: 'ING Bank',
      tip: 'furnizor',
      categorie: 'Cărți și publicații',
      status: 'activ',
      rating: 4.8,
      dataInregistrare: '2019-06-20',
      ultimaTranzactie: '2024-01-25',
      totalTranzactii: 12,
      valoareTotala: 8500,
      sold: 0,
      observatii: 'Specializată în cărți religioase și spirituale',
      parohieId: 'parohia1',
      parohieNume: 'Parohia Sf. Nicolae'
    },
    {
      id: '4',
      nume: 'SC Atelier Icoane SRL',
      cui: 'RO55667788',
      nrRegCom: 'J40/1111/2021',
      adresa: 'Str. Artiștilor nr. 8, București',
      telefon: '021-333-4444',
      email: 'atelier@icoane-religioase.ro',
      contBancar: 'RO44RNCB1234567890123456',
      banca: 'Raiffeisen Bank',
      tip: 'furnizor',
      categorie: 'Arte religioase',
      status: 'activ',
      rating: 4.9,
      dataInregistrare: '2021-02-14',
      ultimaTranzactie: '2024-01-28',
      totalTranzactii: 8,
      valoareTotala: 15000,
      sold: 3000,
      observatii: 'Atelier specializat în icoane și obiecte de cult',
      parohieId: 'parohia1',
      parohieNume: 'Parohia Sf. Nicolae'
    },
    {
      id: '5',
      nume: 'SC Servicii Curatenie SRL',
      cui: 'RO99887766',
      nrRegCom: 'J40/2222/2022',
      adresa: 'Str. Curățeniei nr. 12, București',
      telefon: '021-666-8888',
      email: 'servicii@curatenie-biserica.ro',
      contBancar: 'RO55CECB1234567890123456',
      banca: 'CEC Bank',
      tip: 'furnizor',
      categorie: 'Servicii curățenie',
      status: 'inactiv',
      rating: 3.8,
      dataInregistrare: '2022-04-01',
      ultimaTranzactie: '2023-12-15',
      totalTranzactii: 6,
      valoareTotala: 3200,
      sold: 800,
      observatii: 'Servicii de curățenie pentru spații religioase',
      parohieId: 'parohia2',
      parohieNume: 'Parohia Sf. Gheorghe'
    }
  ]);

  const supplierCategories = [
    'Materiale construcție',
    'Utilități',
    'Cărți și publicații',
    'Arte religioase',
    'Servicii curățenie',
    'Mobilier',
    'Tehnică audio-video',
    'Altele'
  ];

  const filteredSuppliers = suppliers.filter(supplier => {
    const matchesSearch = supplier.nume.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         supplier.cui.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         supplier.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filterTip || supplier.tip === filterTip;
    const matchesCategory = !filterCategorie || supplier.categorie === filterCategorie;
    const matchesStatus = !filterStatus || supplier.status === filterStatus;
    
    return matchesSearch && matchesType && matchesCategory && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'activ': return 'success';
      case 'inactiv': return 'error';
      case 'suspendat': return 'warning';
      default: return 'default';
    }
  };

  const getTypeColor = (tip: string) => {
    switch (tip) {
      case 'furnizor': return 'primary';
      case 'client': return 'secondary';
      case 'ambele': return 'info';
      default: return 'default';
    }
  };

  const handleViewSupplier = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedSupplier(null);
  };

  const totalSuppliers = suppliers.length;
  const activeSuppliers = suppliers.filter(s => s.status === 'activ').length;
  const totalValue = suppliers.reduce((sum, s) => sum + s.valoareTotala, 0);
  const averageRating = suppliers.reduce((sum, s) => sum + s.rating, 0) / suppliers.length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Furnizori
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant={viewMode === 'list' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('list')}
            startIcon={<ViewIcon />}
          >
            Listă
          </Button>
          <Button
            variant={viewMode === 'cards' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('cards')}
            startIcon={<BusinessIcon />}
          >
            Carduri
          </Button>
          {hasPermission('gestiune.write') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {/* Implementare adăugare furnizor nou */}}
            >
              Furnizor nou
            </Button>
          )}
        </Box>
      </Box>

      {/* Statistici furnizori */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <BusinessIcon color="primary" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Total furnizori
                </Typography>
              </Box>
              <Typography variant="h5" color="primary.main">
                {totalSuppliers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AccountBalanceIcon color="success" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Furnizori activi
                </Typography>
              </Box>
              <Typography variant="h5" color="success.main">
                {activeSuppliers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  ⭐
                </Typography>
                <Typography color="textSecondary">
                  Rating mediu
                </Typography>
              </Box>
              <Typography variant="h5" color="warning.main">
                {averageRating.toFixed(1)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  €
                </Typography>
                <Typography color="textSecondary">
                  Valoare totală
                </Typography>
              </Box>
              <Typography variant="h5" color="info.main">
                €{totalValue.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtre pentru furnizori */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Caută furnizori..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Tip</InputLabel>
              <Select
                value={filterTip}
                onChange={(e) => setFilterTip(e.target.value)}
                label="Tip"
              >
                <MenuItem value="">Toate</MenuItem>
                <MenuItem value="furnizor">Furnizor</MenuItem>
                <MenuItem value="client">Client</MenuItem>
                <MenuItem value="ambele">Ambele</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Categorie</InputLabel>
              <Select
                value={filterCategorie}
                onChange={(e) => setFilterCategorie(e.target.value)}
                label="Categorie"
              >
                <MenuItem value="">Toate</MenuItem>
                {supplierCategories.map(category => (
                  <MenuItem key={category} value={category}>{category}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="">Toate</MenuItem>
                <MenuItem value="activ">Activ</MenuItem>
                <MenuItem value="inactiv">Inactiv</MenuItem>
                <MenuItem value="suspendat">Suspendat</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => {
                setSearchTerm('');
                setFilterTip('');
                setFilterCategorie('');
                setFilterStatus('');
              }}
            >
              Resetează
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Vizualizare listă */}
      {viewMode === 'list' && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Furnizor</TableCell>
                <TableCell>CUI</TableCell>
                <TableCell>Tip</TableCell>
                <TableCell>Categorie</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Valoare totală</TableCell>
                <TableCell>Sold</TableCell>
                <TableCell>Ultima tranzacție</TableCell>
                <TableCell>Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSuppliers.map((supplier) => (
                <TableRow key={supplier.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        <BusinessIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {supplier.nume}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {supplier.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{supplier.cui}</TableCell>
                  <TableCell>
                    <Chip
                      label={supplier.tip}
                      color={getTypeColor(supplier.tip) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{supplier.categorie}</TableCell>
                  <TableCell>
                    <Chip
                      label={supplier.status}
                      color={getStatusColor(supplier.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ mr: 1 }}>
                        ⭐ {supplier.rating}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight="bold">
                      €{supplier.valoareTotala.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography 
                      fontWeight="bold" 
                      color={supplier.sold > 0 ? 'success.main' : supplier.sold < 0 ? 'error.main' : 'text.primary'}
                    >
                      €{supplier.sold.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>{supplier.ultimaTranzactie || '-'}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleViewSupplier(supplier)}
                      color="primary"
                    >
                      <ViewIcon />
                    </IconButton>
                    {hasPermission('gestiune.write') && (
                      <>
                        <IconButton color="secondary">
                          <EditIcon />
                        </IconButton>
                        <IconButton color="error">
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Vizualizare carduri */}
      {viewMode === 'cards' && (
        <Grid container spacing={2}>
          {filteredSuppliers.map((supplier) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={supplier.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                      <BusinessIcon />
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" noWrap>
                        {supplier.nume}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {supplier.cui}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Categorie:</strong> {supplier.categorie}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Email:</strong> {supplier.email}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Telefon:</strong> {supplier.telefon}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Adresă:</strong> {supplier.adresa}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Chip
                      label={supplier.status}
                      color={getStatusColor(supplier.status) as any}
                      size="small"
                    />
                    <Typography variant="body2">
                      ⭐ {supplier.rating}
                    </Typography>
                  </Box>

                  <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
                    €{supplier.valoareTotala.toLocaleString()}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color={supplier.sold > 0 ? 'success.main' : supplier.sold < 0 ? 'error.main' : 'text.primary'}
                    sx={{ mb: 1 }}
                  >
                    Sold: €{supplier.sold.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {supplier.totalTranzactii} tranzacții
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <IconButton
                      onClick={() => handleViewSupplier(supplier)}
                      color="primary"
                      size="small"
                    >
                      <ViewIcon />
                    </IconButton>
                    {hasPermission('gestiune.write') && (
                      <>
                        <IconButton color="secondary" size="small">
                          <EditIcon />
                        </IconButton>
                        <IconButton color="error" size="small">
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog pentru vizualizare furnizor */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalii furnizor
        </DialogTitle>
        <DialogContent>
          {selectedSupplier && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ mr: 2, bgcolor: 'primary.main', width: 56, height: 56 }}>
                    <BusinessIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {selectedSupplier.nume}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {selectedSupplier.categorie}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Chip
                        label={selectedSupplier.tip}
                        color={getTypeColor(selectedSupplier.tip) as any}
                        size="small"
                      />
                      <Chip
                        label={selectedSupplier.status}
                        color={getStatusColor(selectedSupplier.status) as any}
                        size="small"
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Informații de contact
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <EmailIcon />
                    </ListItemIcon>
                    <ListItemText primary="Email" secondary={selectedSupplier.email} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <PhoneIcon />
                    </ListItemIcon>
                    <ListItemText primary="Telefon" secondary={selectedSupplier.telefon} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <LocationIcon />
                    </ListItemIcon>
                    <ListItemText primary="Adresă" secondary={selectedSupplier.adresa} />
                  </ListItem>
                </List>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Informații juridice
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="CUI" secondary={selectedSupplier.cui} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Nr. Reg. Com." secondary={selectedSupplier.nrRegCom} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Bancă" secondary={selectedSupplier.banca} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Cont bancar" secondary={selectedSupplier.contBancar} />
                  </ListItem>
                </List>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Statistici
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="Rating" 
                      secondary={`${selectedSupplier.rating}/5 ⭐`} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Total tranzacții" 
                      secondary={selectedSupplier.totalTranzactii} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Valoare totală" 
                      secondary={`€${selectedSupplier.valoareTotala.toLocaleString()}`} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Sold" 
                      secondary={`€${selectedSupplier.sold.toLocaleString()}`} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Ultima tranzacție" 
                      secondary={selectedSupplier.ultimaTranzactie || 'Nu există'} 
                    />
                  </ListItem>
                </List>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Istoric
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="Data înregistrare" 
                      secondary={selectedSupplier.dataInregistrare} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Parohie" 
                      secondary={selectedSupplier.parohieNume || 'Toate parohiile'} 
                    />
                  </ListItem>
                </List>
              </Grid>

              {selectedSupplier.observatii && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Observații
                  </Typography>
                  <Typography variant="body1">
                    {selectedSupplier.observatii}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Închide</Button>
          {hasPermission('gestiune.write') && (
            <Button variant="contained" startIcon={<EditIcon />}>
              Editează
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Furnizori;
